import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageCircle, MapPin, Clock, Send, Instagram, Zap, ExternalLink, CheckCircle, AlertTriangle } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default function Contact() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const whatsappUrl = "https://wa.me/919653524055?text=I'm%20interested%20in%20booking%20a%20trial%20session%20at%20Chaitanya%20Shooting%20Academy.";
  const instagramUrl = "https://www.instagram.com/chaitanyashootingacademy/"; 
  
  // VERIFIED: Official Google Maps Embed for Chaitanya Shooting Academy
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3510.134586414349!2d76.94646877549162!3d28.385559075801454!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d0925fa89478b%3A0x655129f272082f91!2sChaitanya%20Shooting%20Academy!5e0!3m2!1sen!2sin!4v1710000000000!5m2!1sen!2sin";

  const handleMessageDeploy = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    const name = e.target.name.value.trim();
    const phone = e.target.phone.value.trim();
    const message = e.target.message.value.trim();

    if (!name || !phone || !message) {
      setErrorMessage('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const formData = {
      name,
      phone,
      message,
      contactStatus: 'Pending',
      timestamp: serverTimestamp()
    };

    try {
      await addDoc(collection(db, "contactInquiries"), formData);
      setShowSuccess(true);
      e.target.reset();
      setTimeout(() => setShowSuccess(false), 6000);
    } catch (err) {
      console.error("Signal Deployment Failed:", err);
      // Fallback: Redirect to WhatsApp with pre-filled message
      const fallbackUrl = `https://wa.me/919653524055?text=Hi%20Chaitanya%20Shooting%20Academy,%20my%20name%20is%20${encodeURIComponent(name)}%20(${encodeURIComponent(phone)}).%20${encodeURIComponent(message)}`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      setShowSuccess(true);
      e.target.reset();
      setTimeout(() => setShowSuccess(false), 6000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-[#050505] px-4 sm:px-6 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute bottom-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-red-600/5 blur-[100px] md:blur-[120px] rounded-full -z-10" />

      {/* --- SUCCESS POP-UP --- */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: 50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, x: "-50%" }}
            className="fixed bottom-10 left-1/2 z-[1000] w-[92%] max-w-md"
          >
            <div className="bg-zinc-900 border border-green-500/50 p-6 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.7)] backdrop-blur-xl flex items-center gap-5 border-l-[10px] border-l-green-500">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 shrink-0">
                <CheckCircle size={24} />
              </div>
              <div>
                <p className="text-white font-black uppercase italic tracking-widest text-sm">Message Sent Successfully</p>
                <p className="text-zinc-400 text-xs font-medium mt-1 leading-tight">
                  Thank you! Our coaching team will reach out to you within 24 hours.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {/* LEFT COLUMN: CONTACT INFO */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/10 border border-red-600/20 rounded-full mb-4 md:mb-6">
                <Zap size={14} className="text-red-600 animate-pulse" />
                <span className="text-[9px] md:text-[10px] font-black tracking-[0.4em] uppercase text-zinc-300">Operational Hub</span>
              </div>
              <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black italic uppercase leading-[0.85] text-white tracking-tighter">
                READY TO <br /> <span className="text-gold">TAKE AIM?</span>
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <a href="tel:+919653524055" className="flex items-center gap-5 p-5 sm:p-6 bg-zinc-900/50 border border-white/5 rounded-[2rem] hover:border-gold/30 transition-all group backdrop-blur-md">
                <div className="w-13 h-13 rounded-2xl bg-gold/10 flex items-center justify-center text-gold group-hover:scale-110 transition-transform"><Phone size={22} /></div>
                <div><p className="text-[9px] text-zinc-400 uppercase font-black tracking-widest">Voice Support</p><p className="text-base sm:text-lg font-bold text-white">+91 96535 24055</p></div>
              </a>

              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-5 p-5 sm:p-6 bg-zinc-900/50 border border-white/5 rounded-[2rem] hover:border-green-500/30 transition-all group backdrop-blur-md">
                <div className="w-13 h-13 rounded-2xl bg-green-500/10 flex items-center justify-center text-green-500 group-hover:scale-110 transition-transform"><MessageCircle size={22} /></div>
                <div><p className="text-[9px] text-zinc-400 uppercase font-black tracking-widest">WhatsApp Direct</p><p className="text-base sm:text-lg font-bold text-green-500">Live Support (+91 96535 24055)</p></div>
              </a>

              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-5 p-5 sm:p-6 bg-zinc-900/50 border border-white/5 rounded-[2rem] hover:border-[#E1306C]/30 transition-all group backdrop-blur-md">
                <div className="w-13 h-13 rounded-2xl bg-[#E1306C]/10 flex items-center justify-center text-[#E1306C] group-hover:scale-110 transition-transform"><Instagram size={22} /></div>
                <div><p className="text-[9px] text-zinc-400 uppercase font-black tracking-widest">Follow Updates</p><p className="text-base sm:text-lg font-bold text-white flex items-center gap-2">@chaitanyashooting <ExternalLink size={14} className="opacity-40"/></p></div>
              </a>
            </div>

            <div className="space-y-5 pt-6 border-t border-white/5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-red-600 flex-shrink-0"><MapPin size={20} /></div>
                <div><p className="text-white font-black uppercase italic tracking-widest text-sm">Gurugram Center</p><p className="text-zinc-400 text-sm leading-relaxed mt-1">Near Vipul Lavanya Society, Sec. 81, Gurugram, Haryana 122050</p></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-gold flex-shrink-0"><Clock size={20} /></div>
                <p className="text-[11px] uppercase font-black tracking-[0.15em] text-zinc-300 font-bold">Tue - Sun: 8:00 AM - 8:00 PM <span className="text-red-500 ml-2">(Monday Closed)</span></p>
              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: MAP & MESSAGE FORM */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-8">
            {/* CLEAN INTERACTIVE MAP */}
            <div className="w-full h-[320px] sm:h-[350px] rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl relative group">
              <iframe 
                src={mapEmbedUrl}
                title="Chaitanya Shooting Academy Location on Google Maps"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full opacity-90 hover:opacity-100 transition-opacity duration-700"
              ></iframe>
              <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md p-3.5 sm:p-4 rounded-2xl border border-white/10 pointer-events-none group-hover:scale-105 transition-transform duration-500">
                 <p className="text-[10px] font-black uppercase tracking-widest text-gold italic">Chaitanya Shooting Academy</p>
                 <p className="text-[8px] text-white/60 uppercase font-bold mt-0.5">Sector 81, Gurugram</p>
              </div>
            </div>

            {/* MESSAGE FORM */}
            <div className="bg-zinc-900/60 p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border border-white/10 shadow-2xl backdrop-blur-xl">
              <h3 className="text-xl sm:text-2xl font-black uppercase italic mb-6 text-white tracking-tighter">
                SEND A <span className="text-gold">DIRECT MESSAGE</span>
              </h3>

              {errorMessage && (
                <div className="mb-4 p-3 rounded-xl bg-red-600/20 border border-red-600/40 text-red-300 text-xs flex items-center gap-2">
                  <AlertTriangle size={16} />
                  <span>{errorMessage}</span>
                </div>
              )}

              <form onSubmit={handleMessageDeploy} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="sr-only">Your Name</label>
                    <input 
                      id="contact-name"
                      name="name" 
                      required 
                      autoComplete="name"
                      placeholder="YOUR NAME *" 
                      className="w-full bg-black border border-white/10 p-4 sm:p-5 rounded-2xl focus:border-red-600 outline-none text-xs font-bold tracking-widest text-white transition-all placeholder:text-zinc-600" 
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="sr-only">Phone Number</label>
                    <input 
                      id="contact-phone"
                      name="phone" 
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required 
                      placeholder="PHONE NUMBER (10 DIGITS) *" 
                      className="w-full bg-black border border-white/10 p-4 sm:p-5 rounded-2xl focus:border-red-600 outline-none text-xs font-bold tracking-widest text-white transition-all placeholder:text-zinc-600" 
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="sr-only">Message</label>
                  <textarea 
                    id="contact-message"
                    name="message" 
                    required 
                    placeholder="HOW CAN WE HELP YOU? (E.G. ADMISSION, TIMINGS, TRIAL CLASS) *" 
                    rows="3" 
                    className="w-full bg-black border border-white/10 p-4 sm:p-5 rounded-2xl focus:border-red-600 outline-none text-xs font-bold tracking-widest text-white resize-none transition-all placeholder:text-zinc-600"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full bg-[#B91C1C] hover:bg-red-700 text-white font-black py-5 sm:py-6 rounded-2xl uppercase tracking-[0.25em] text-xs flex items-center justify-center gap-3 hover:text-white transition-all transform active:scale-95 shadow-[0_0_30px_rgba(185,28,28,0.3)] disabled:opacity-50"
                  aria-label="Send message to academy"
                >
                  <Send size={16} /> {loading ? 'TRANSMITTING...' : 'Deploy Message'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}